/**
 * Parlant event normalization and transport error classification.
 *
 * Official generated clients expose camelCase DTO fields while raw REST/SSE
 * responses commonly use snake_case. Normalize once at the boundary.
 */

import type { EventData, EventKind, MessageSource, ParlantEvent } from '../types/parlant';

function readString(raw: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

function readNumber(raw: Record<string, unknown>, key: string): number | undefined {
  const value = raw[key];
  return typeof value === 'number' ? value : undefined;
}

/**
 * Normalizes a raw Parlant event from REST, SSE, or generated client DTOs
 * into the canonical snake_case `ParlantEvent` shape used internally.
 */
export function normalizeParlantEvent(raw: Record<string, unknown>): ParlantEvent {
  const correlationOrTrace =
    readString(raw, 'correlation_id', 'correlationId', 'trace_id', 'traceId') ?? undefined;

  return {
    id: readString(raw, 'id'),
    kind: raw.kind as EventKind,
    source: raw.source as MessageSource,
    offset: readNumber(raw, 'offset') ?? 0,
    correlation_id: correlationOrTrace,
    trace_id: readString(raw, 'trace_id', 'traceId') ?? correlationOrTrace,
    creation_utc:
      readString(raw, 'creation_utc', 'creationUtc') ?? new Date().toISOString(),
    data: (raw.data ?? {}) as EventData,
    metadata: (raw.metadata as Record<string, unknown> | undefined) ?? {},
    deleted: raw.deleted === true,
  };
}

/**
 * Normalizes an array of raw events.
 */
export function normalizeParlantEvents(
  rawEvents: Record<string, unknown>[]
): ParlantEvent[] {
  return rawEvents.map(normalizeParlantEvent);
}

/**
 * Returns true when a transport error should stop retry/demotion and surface
 * to the consumer (auth, config, or missing session).
 */
export function isFatalTransportError(message: string): boolean {
  return (
    /\b401\b/.test(message) ||
    /\b403\b/.test(message) ||
    /\b422\b/.test(message) ||
    message.includes('Session not found') ||
    message.includes('session may have been deleted')
  );
}
